import React, { useState, useEffect, useRef } from "react";
import api from "../api/client";
import Modal, { FormField, FormRow, Input, Select, TextArea, SubmitButton, ErrorBanner } from "../components/Modal";

/**
 * DealIntelligenceModal
 * 
 * 3-step flow:
 * 1. Upload one or more documents (PDF/DOCX)
 * 2. AI parses it → user reviews/edits extracted terms
 * 3. Save to database (creates Securities + Deals)
 */
export default function DealIntelligenceModal({ onClose, onSaved }) {
  const [step, setStep] = useState(1); // 1=upload, 2=review, 3=saving
  const [files, setFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef();

  // ── Step 1: Upload ──

  const handleUpload = async () => {
    if (files.length === 0) return setError("Please select at least one file");
    setError("");
    setParsing(true);

    const formData = new FormData();
    files.forEach((f) => formData.append("file", f));

    try {
      const res = await api.post("/securities/parse-document/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 min timeout for AI parsing
      });
      setParsedData(res.data);
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.error || "Parsing failed. Try again.";
      setError(msg);
    } finally {
      setParsing(false);
    }
  };

  if (step === 1) {
    return (
      <Modal title="🤖 Deal Intelligence — Upload Documents" onClose={onClose} width={520}>
        <ErrorBanner message={error} />
        <div style={{
          border: "2px dashed #d1d5db", borderRadius: 12, padding: 40,
          textAlign: "center", marginBottom: 16, cursor: "pointer",
          background: files.length > 0 ? "#f0f9ff" : "#fafafa",
        }}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)); }}
        >
          <input
            ref={fileRef}
            type="file" multiple
            accept=".pdf,.docx,.doc,.txt"
            style={{ display: "none" }}
            onChange={(e) => setFiles(Array.from(e.target.files))}
          />
          {files.length > 0 ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{files.map(f => f.name).join(", ")}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                {(files.reduce((a, f) => a + f.size, 0) / 1024).toFixed(0)} KB ({files.length} file{files.length !== 1 ? "s" : ""}) — Click to change
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>
                Drop a document here or click to browse
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                Supports: PDF, DOCX, TXT (max 25MB)
              </div>
            </div>
          )}
        </div>

        <div style={{
          background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 16,
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
            Supported Document Types
          </div>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
            • Securities Purchase Agreements (SPAs)<br />
            • Form of Convertible Promissory Note<br />
            • Form of Common Stock Purchase Warrant<br />
            • Equity Line of Credit / Purchase Agreements<br />
            • Registration Rights Agreements
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{
            padding: "10px 20px", borderRadius: 6, border: "1px solid #d1d5db",
            background: "#fff", color: "#334155", fontSize: 13, cursor: "pointer",
          }}>Cancel</button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || parsing}
            style={{
              padding: "10px 24px", borderRadius: 6, border: "none",
              background: files.length === 0 || parsing ? "#94a3b8" : "#2563eb",
              color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: files.length === 0 || parsing ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {parsing ? (
              <>
                <span style={{
                  display: "inline-block", width: 14, height: 14,
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff", borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }} />
                Analyzing Document...
              </>
            ) : (
              "🤖 Parse with AI"
            )}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </Modal>
    );
  }

  // ── Step 2: Review ──

  if (step === 2 && parsedData) {
    return (
      <ReviewTermsModal
        parsedData={parsedData}
        setParsedData={setParsedData}
        onClose={onClose}
        onSaved={onSaved}
        filename={files.map(f => f.name).join(", ")}
      />
    );
  }

  return null;
}


// ═══════════════════════════════════════════════════════════════
// Review & Edit Modal
// ═══════════════════════════════════════════════════════════════

function ReviewTermsModal({ parsedData, setParsedData, onClose, onSaved, filename }) {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [dealName, setDealName] = useState("");
  const [strategyTag, setStrategyTag] = useState(parsedData?.deal_terms?.strategy_tag || "PIPE");
  const [securities, setSecurities] = useState(parsedData?.securities_to_create || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    api.get("/core/entities/").then((r) => {
      const ents = r.data.results || r.data;
      setEntities(ents);
      if (ents.length > 0) setEntityId(ents[0].id);
    });
    // Auto-generate deal name from issuer
    if (parsedData?.parties?.issuer?.name) {
      setDealName(`${parsedData.parties.issuer.name} ${strategyTag}`);
    }
  }, []);

  const updateSecurity = (idx, field, value) => {
    const next = [...securities];
    next[idx] = { ...next[idx], [field]: value };
    setSecurities(next);
  };

  const updateNestedField = (idx, section, field, value) => {
    const next = [...securities];
    next[idx] = {
      ...next[idx],
      [section]: { ...next[idx][section], [field]: value },
    };
    setSecurities(next);
  };

  const removeSecurity = (idx) => {
    setSecurities(securities.filter((_, i) => i !== idx));
    if (activeTab >= securities.length - 1) setActiveTab(Math.max(0, securities.length - 2));
  };

  const handleSave = async () => {
    if (!entityId) return setError("Select an entity");
    if (!dealName) return setError("Enter a deal name");
    if (securities.length === 0) return setError("At least one security is required");

    setSaving(true);
    setError("");

    // Flatten nested terms into top-level fields for the API
    const flatSecurities = securities.map((sec) => {
      const flat = {
        instrument_type: sec.instrument_type,
        suggested_ticker: sec.suggested_ticker,
        ticker: sec.suggested_ticker,
        name: sec.name,
        issuer: sec.issuer,
      };

      if (sec.instrument_type === "Note" && sec.note_terms) {
        Object.entries(sec.note_terms).forEach(([k, v]) => {
          if (v !== null && v !== "" && !Array.isArray(v)) flat[k] = v;
        });
      }
      if (sec.instrument_type === "Warrant" && sec.warrant_terms) {
        Object.entries(sec.warrant_terms).forEach(([k, v]) => {
          if (v !== null && v !== "") flat[k] = v;
        });
      }
      if (sec.instrument_type === "ELOC" && sec.eloc_terms) {
        const et = sec.eloc_terms;
        flat.eloc_commitment_amount = et.commitment_amount;
        flat.eloc_discount_pct = et.discount_pct_tier1;
        flat.eloc_discount_pct_2 = et.discount_pct_tier2;
        flat.eloc_pricing_days = et.pricing_days;
        flat.eloc_settlement_days = et.settlement_days;
        flat.eloc_max_advance = et.max_advance_per_notice;
        flat.eloc_ownership_limit_pct = et.ownership_limit_pct;
        flat.eloc_exchange = et.exchange;
      }
      if (sec.instrument_type === "Stock" && sec.stock_terms) {
        Object.entries(sec.stock_terms).forEach(([k, v]) => {
          if (v !== null && v !== "") flat[k] = v;
        });
      }

      // Remove nulls/empties
      return Object.fromEntries(Object.entries(flat).filter(([_, v]) => v != null && v !== ""));
    });

    try {
      const res = await api.post("/securities/save-parsed-deal/", {
        entity_id: entityId,
        deal_name: dealName,
        strategy_tag: strategyTag,
        securities: flatSecurities,
      });
      onSaved();
    } catch (err) {
      setSaving(false);
      const msg = err.response?.data?.error || "Save failed";
      setError(msg);
    }
  };

  const sec = securities[activeTab];

  return (
    <Modal title="🤖 Review Extracted Terms" onClose={onClose} width={680}>
      <ErrorBanner message={error} />

      {/* Document Summary */}
      <div style={{
        background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8,
        padding: 14, marginBottom: 16,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#166534", marginBottom: 4 }}>
          {parsedData.document_type || "Document"} — {filename}
        </div>
        <div style={{ fontSize: 12, color: "#15803d", lineHeight: 1.5 }}>
          {parsedData.document_summary}
        </div>
      </div>

      {/* Warnings */}
      {parsedData.warnings && parsedData.warnings.length > 0 && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: 12, marginBottom: 16, fontSize: 12, color: "#92400e",
        }}>
          <strong>⚠️ Review needed:</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {parsedData.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Deal-level fields */}
      <FormRow>
        <FormField label="Deal Name" required>
          <Input value={dealName} onChange={setDealName} />
        </FormField>
        <FormField label="Strategy" required>
          <Select value={strategyTag} onChange={setStrategyTag} options={[
            { value: "PIPE", label: "PIPE" },
            { value: "ELOC", label: "ELOC" },
            { value: "Convertible", label: "Convertible" },
            { value: "Direct", label: "Direct" },
          ]} />
        </FormField>
      </FormRow>
      <FormField label="Entity" required>
        <Select value={entityId} onChange={setEntityId}
          options={entities.map((e) => ({ value: e.id, label: e.name }))}
          placeholder="Select entity..."
        />
      </FormField>

      {/* Securities tabs */}
      <div style={{ borderTop: "1px solid #e5e7eb", margin: "16px 0 12px", paddingTop: 12 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {securities.map((s, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(idx)}
              style={{
                padding: "6px 12px", borderRadius: 6,
                border: activeTab === idx ? "2px solid #2563eb" : "1px solid #d1d5db",
                background: activeTab === idx ? "#eff6ff" : "#fff",
                color: activeTab === idx ? "#1d4ed8" : "#334155",
                fontSize: 12, fontWeight: 500, cursor: "pointer",
              }}
            >
              {s.instrument_type}: {s.suggested_ticker || s.name?.slice(0, 15) || `Security ${idx + 1}`}
            </button>
          ))}
        </div>
      </div>

      {/* Active security editor */}
      {sec && (
        <SecurityEditor
          sec={sec}
          idx={activeTab}
          update={updateSecurity}
          updateNested={updateNestedField}
          onRemove={securities.length > 1 ? () => removeSecurity(activeTab) : null}
        />
      )}

      {/* Key Dates */}
      {parsedData.key_dates && parsedData.key_dates.length > 0 && (
        <div style={{
          background: "#f8fafc", borderRadius: 8, padding: 12, marginTop: 12,
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Key Dates</div>
          {parsedData.key_dates.map((kd, i) => (
            <div key={i} style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>
              <strong>{kd.date}</strong> — {kd.description}
            </div>
          ))}
        </div>
      )}

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {securities.length} {securities.length === 1 ? "security" : "securities"} will be created
        </div>
        <SubmitButton onClick={handleSave} loading={saving} label="💾 Save to Database" />
      </div>
    </Modal>
  );
}


// ═══════════════════════════════════════════════════════════════
// Per-Security Editor
// ═══════════════════════════════════════════════════════════════

function SecurityEditor({ sec, idx, update, updateNested, onRemove }) {
  const type = sec.instrument_type;

  return (
    <div style={{
      background: "#fafafa", borderRadius: 8, padding: 14,
      border: "1px solid #e5e7eb",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
          {type} Details
        </div>
        {onRemove && (
          <button onClick={onRemove} style={{
            background: "none", border: "1px solid #fca5a5", color: "#dc2626",
            borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer",
          }}>Remove</button>
        )}
      </div>

      {/* Common fields */}
      <FormRow>
        <FormField label="Ticker">
          <Input value={sec.suggested_ticker || ""} onChange={(v) => update(idx, "suggested_ticker", v)} />
        </FormField>
        <FormField label="Type">
          <Select value={type} onChange={(v) => update(idx, "instrument_type", v)} options={[
            { value: "Stock", label: "Stock" },
            { value: "Note", label: "Note" },
            { value: "Warrant", label: "Warrant" },
            { value: "ELOC", label: "ELOC" },
          ]} />
        </FormField>
      </FormRow>
      <FormField label="Name">
        <Input value={sec.name || ""} onChange={(v) => update(idx, "name", v)} />
      </FormField>
      <FormField label="Issuer">
        <Input value={sec.issuer || ""} onChange={(v) => update(idx, "issuer", v)} />
      </FormField>

      {/* Note fields */}
      {type === "Note" && sec.note_terms && (
        <>
          <FormRow>
            <FormField label="Principal">
              <Input type="number" value={sec.note_terms.principal_amount || ""}
                onChange={(v) => updateNested(idx, "note_terms", "principal_amount", v)} />
            </FormField>
            <FormField label="Coupon Rate">
              <Input type="number" value={sec.note_terms.coupon_rate || ""}
                onChange={(v) => updateNested(idx, "note_terms", "coupon_rate", v)} step="0.001" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Issue Date">
              <Input type="date" value={sec.note_terms.issue_date || ""}
                onChange={(v) => updateNested(idx, "note_terms", "issue_date", v)} />
            </FormField>
            <FormField label="Maturity Date">
              <Input type="date" value={sec.note_terms.maturity_date || ""}
                onChange={(v) => updateNested(idx, "note_terms", "maturity_date", v)} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Conversion Price">
              <Input type="number" value={sec.note_terms.conversion_price || ""}
                onChange={(v) => updateNested(idx, "note_terms", "conversion_price", v)} step="0.01" />
            </FormField>
            <FormField label="Day Count">
              <Select value={sec.note_terms.day_count || "30/360"}
                onChange={(v) => updateNested(idx, "note_terms", "day_count", v)}
                options={[
                  { value: "30/360", label: "30/360" },
                  { value: "Actual/360", label: "Actual/360" },
                  { value: "Actual/365", label: "Actual/365" },
                ]} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Interest Mode">
              <Select value={sec.note_terms.interest_mode || "Cash"}
                onChange={(v) => updateNested(idx, "note_terms", "interest_mode", v)}
                options={[
                  { value: "Cash", label: "Cash" },
                  { value: "PIK", label: "PIK" },
                  { value: "Cash/PIK", label: "Cash/PIK" },
                ]} />
            </FormField>
            <FormField label="Conversion Ratio">
              <Input type="number" value={sec.note_terms.conversion_ratio || ""}
                onChange={(v) => updateNested(idx, "note_terms", "conversion_ratio", v)} />
            </FormField>
          </FormRow>
          {sec.note_terms.events_of_default && sec.note_terms.events_of_default.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Events of Default</div>
              <ul style={{ fontSize: 11, color: "#64748b", margin: 0, paddingLeft: 16 }}>
                {sec.note_terms.events_of_default.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Warrant fields */}
      {type === "Warrant" && sec.warrant_terms && (
        <>
          <FormRow>
            <FormField label="Strike Price">
              <Input type="number" value={sec.warrant_terms.strike_price || ""}
                onChange={(v) => updateNested(idx, "warrant_terms", "strike_price", v)} step="0.01" />
            </FormField>
            <FormField label="Shares">
              <Input type="number" value={sec.warrant_terms.warrant_shares || ""}
                onChange={(v) => updateNested(idx, "warrant_terms", "warrant_shares", v)} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Expiry Date">
              <Input type="date" value={sec.warrant_terms.warrant_expiry || ""}
                onChange={(v) => updateNested(idx, "warrant_terms", "warrant_expiry", v)} />
            </FormField>
            <FormField label="Exercise Type">
              <Select value={sec.warrant_terms.exercise_type || "Both"}
                onChange={(v) => updateNested(idx, "warrant_terms", "exercise_type", v)}
                options={[
                  { value: "Cash", label: "Cash Only" },
                  { value: "Cashless", label: "Cashless Only" },
                  { value: "Both", label: "Cash + Cashless" },
                ]} />
            </FormField>
          </FormRow>
        </>
      )}

      {/* ELOC fields */}
      {type === "ELOC" && sec.eloc_terms && (
        <>
          <FormRow>
            <FormField label="Commitment Amount">
              <Input type="number" value={sec.eloc_terms.commitment_amount || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "commitment_amount", v)} />
            </FormField>
            <FormField label="Discount Tier 1">
              <Input type="number" value={sec.eloc_terms.discount_pct_tier1 || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "discount_pct_tier1", v)} step="0.001" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Pricing Days">
              <Input type="number" value={sec.eloc_terms.pricing_days || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "pricing_days", v)} />
            </FormField>
            <FormField label="Settlement Days">
              <Input type="number" value={sec.eloc_terms.settlement_days || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "settlement_days", v)} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Ownership Limit %">
              <Input type="number" value={sec.eloc_terms.ownership_limit_pct || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "ownership_limit_pct", v)} step="0.001" />
            </FormField>
            <FormField label="Exchange">
              <Input value={sec.eloc_terms.exchange || ""}
                onChange={(v) => updateNested(idx, "eloc_terms", "exchange", v)} />
            </FormField>
          </FormRow>
        </>
      )}

      {/* Stock fields */}
      {type === "Stock" && sec.stock_terms && (
        <>
          <FormRow>
            <FormField label="Shares">
              <Input type="number" value={sec.stock_terms.shares || ""}
                onChange={(v) => updateNested(idx, "stock_terms", "shares", v)} />
            </FormField>
            <FormField label="Price Per Share">
              <Input type="number" value={sec.stock_terms.price_per_share || ""}
                onChange={(v) => updateNested(idx, "stock_terms", "price_per_share", v)} step="0.01" />
            </FormField>
          </FormRow>
        </>
      )}
    </div>
  );
}
